import {
  React, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Cookies from 'js-cookie';
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import Label from '../components/Label';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SnackBar from '../components/Snackbar';
import CustomPagination from '../components/CustomPagination';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import AdminMoreMenu from '../sections/@dashboard/user/AdminMoreMenu';
import CreateButton from '../components/CreateButton';
import {
  APIGetPromoCodes,
  APIDeletePromoCode,
  APICreatePromoCode,
  APIGetOperator,
} from '../utils/apiCalled';
import { usePrivilege } from '../PrivilegeContext';
import MorePromoCodes from '../components/dashboard/MorePromoCodes';
import CreatePromoCodeModal from '../components/dashboard/CreatePromoCodeModal';
import PagePromoCode from '../components/dashboard/PagePromoCode';

const TABLE_HEAD = [
  { id: 'code', label: 'Code Name', alignRight: false },
  { id: 'type', label: 'Type', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'assignedOperators', label: 'Assigned Operators', alignRight: false },
  { id: 'validityPeriodDays', label: 'Validity (Days)', alignRight: false },
  { id: 'discountPercentage', label: 'Discount Percentage (%)', alignRight: false },
  { id: 'actions', label: '', alignRight: true },
];

const tableStyle = {
  border: '0.063rem solid #EAECF0',
  borderTop: '0.094rem solid #EAECF0',
  borderBottom: '0.094rem solid #EAECF0',
  boxShadow: '0rem 0.063rem 0.188rem rgba(16, 24, 40, 0.1), 0rem 0.063rem 0.125rem rgba(16, 24, 40, 0.06)',
  borderRadius: '0.75rem',
};

const loadingStyle = {
  position: 'absolute',
  zIndex: 10,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function PromoCodes() {
  const theme = useTheme();
  const { role } = usePrivilege();

  const [promoCodes, setPromoCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('code');
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [filterName, setFilterName] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState('info');
  const [mainMsg, setMainMsg] = useState('');
  const [succMsg, setSuccMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [showEditPage, setShowEditPage] = useState(false);
  const [editPromoCode, setEditPromoCode] = useState(null);
  const [modalTriggerAPICalled, setModalTriggerAPICalled] = useState(false);
  const [operatorsMap, setOperatorsMap] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingPromoCode, setCreatingPromoCode] = useState(false);
  const [statusFilter] = useState('');
  const [zoneFilter] = useState('');
  const [typeFilter] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const isMountedRef = useRef(true);

  const canManagePromoCodes = role === 900 || role === 101 || role === 111 || role === 102;

  const fetchPromoCodes = useCallback(async (preserveSnackbar = false) => {
    const params = {
      limit,
      skip: page * limit,
      role,
    };

    // Add userOid for non-admin roles (required by backend)
    if (role !== 101 && role !== 900) {
      const userOid = Cookies.get('userId');
      if (userOid) {
        params.userOid = userOid;
      }
    }

    // Add search parameter if filterName has a value
    if (filterName && filterName.trim()) {
      params.searchString = filterName.trim();
    }

    // Add status filter
    if (statusFilter) {
      params.status = statusFilter;
    }

    // Add zone filter
    if (zoneFilter) {
      params.zone = zoneFilter;
    }

    // Add type filter
    if (typeFilter) {
      params.type = typeFilter;
    }

    // Add operator filter if an operator has been selected
    const selectedOperatorId = Cookies.get('selectedOperatorId');
    if (selectedOperatorId) {
      params.operatorOID = selectedOperatorId;
    }

    try {
      const result = await APIGetPromoCodes(setLoading, params);
      if (!isMountedRef.current) return;

      if (result.status === 200) {
        const payload = result.data?.data || {};
        setPromoCodes(payload.promoCodes || []);
        setTotal(payload.total ?? payload.count ?? 0);
        if (!preserveSnackbar) {
          setVariant('success');
          setMainMsg('Promo codes refreshed');
          setSuccMsg(result.data?.message || 'Promo codes loaded successfully.');
          setErrMsg('');
          setOpen(false);
        }
      } else {
        throw result;
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      const response = error?.response;
      setPromoCodes([]);
      setTotal(0);
      setVariant('error');
      setMainMsg('Failed to load promo codes');
      const message = response?.data?.message || error?.message || 'Unable to retrieve promo codes.';
      setErrMsg(message);
      setSuccMsg('');
      setOpen(true);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [limit, page, filterName, statusFilter, zoneFilter, typeFilter, role]);

  // Cleanup on unmount
  useEffect(() => () => {
    isMountedRef.current = false;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPromoCodes();
  }, []);

  // Handle pagination changes (page, limit) - fetch immediately
  useEffect(() => {
    // Skip if this is the initial render (filterName might trigger separately)
    if (page !== undefined) {
      fetchPromoCodes();
    }
  }, [page, limit]);

  // Debounced search effect - watches filterName and calls API after 600ms delay
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setPage(0); // Reset to first page when searching
        fetchPromoCodes();
      }
    }, 600);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filterName]);

  // Handle filter changes
  useEffect(() => {
    if (isMountedRef.current) {
      setPage(0);
      fetchPromoCodes();
    }
  }, [statusFilter, zoneFilter, typeFilter]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    if (newPage < 0) {
      return;
    }
    setPage(newPage);
  };

  const filteredPromoCodes = useMemo(
    () => applySortFilter(promoCodes, getComparator(order, orderBy)),
    [promoCodes, order, orderBy],
  );

  const handleRowClick = (promoCode) => {
    setSelectedPromoCode(promoCode);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedPromoCode(null);
  };

  const handleSnackBarClose = () => {
    setOpen(false);
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleCreatePromoCode = useCallback(
    async (payload) => {
      setCreatingPromoCode(true);
      try {
        const result = await APICreatePromoCode(() => {}, payload, role);
        if (result.status === 200) {
          setVariant('success');
          setMainMsg('Promo code created successfully');
          setSuccMsg(result.data?.message || 'The promo code has been created.');
          setErrMsg('');
          setOpen(true);
          setCreateModalOpen(false);
          await fetchPromoCodes(true);
        } else {
          const response = result?.response;
          const message = response?.data?.message
            || result?.message
            || 'Unable to create promo code.';
          setVariant('error');
          setMainMsg('Failed to create promo code');
          setErrMsg(message);
          setSuccMsg('');
          setOpen(true);
        }
      } catch (error) {
        const response = error?.response;
        const message = response?.data?.message || error?.message || 'Unable to create promo code.';
        setVariant('error');
        setMainMsg('Failed to create promo code');
        setErrMsg(message);
        setSuccMsg('');
        setOpen(true);
      } finally {
        setCreatingPromoCode(false);
      }
    },
    [fetchPromoCodes, role],
  );

  const handleOpenEditPage = (promoCode) => {
    setEditPromoCode(promoCode);
    setShowEditPage(true);
  };

  const handleCloseEditPage = useCallback(() => {
    setShowEditPage(false);
    setEditPromoCode(null);
    fetchPromoCodes(true);
  }, [fetchPromoCodes]);

  const handleOpenDeleteModal = () => {
    setDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
  };

  const handleDelete = async (promoCode) => {
    try {
      const selectedOperatorId = Cookies.get('selectedOperatorId');
      const result = await APIDeletePromoCode(setLoading, promoCode.code, role, selectedOperatorId);
      if (result.status === 200) {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setVariant('success');
        setMainMsg('Promo Code Successfully Deleted');
        setSuccMsg(`Promo code ${promoCode.code} has been successfully deleted`);
        setModalTriggerAPICalled(true);
        setShowDetails(false);
        setSelectedPromoCode(null);
      } else {
        setLoading(false);
        setDeleteModal(false);
        setOpen(true);
        setVariant('error');
        setMainMsg('Error Deleting Promo Code');
        setErrMsg('Error deleting promo code!, Please try again');
      }
    } catch (err) {
      setLoading(false);
      setDeleteModal(false);
      setOpen(true);
      setVariant('error');
      setMainMsg('Error Deleting Promo Code');
      setErrMsg('Error deleting promo code!, Please try again');
    }
  };

  // Refresh promo codes when modal is triggered after API call
  useEffect(() => {
    if (modalTriggerAPICalled) {
      fetchPromoCodes(true);
      setModalTriggerAPICalled(false);
    }
  }, [modalTriggerAPICalled, fetchPromoCodes]);

  // Fetch operators to create map
  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const result = await APIGetOperator(() => {});
        if (result?.data) {
          const map = {};
          result.data.forEach((op) => {
            map[op._id] = op.operatorName;
          });
          setOperatorsMap(map);
        }
      } catch (error) {
        // Error fetching operators
      }
    };
    fetchOperators();
  }, []);

  return (
    <Page title="Promo Codes">
      <Container maxWidth="xl" sx={{ mt: showDetails || showEditPage ? 0 : 12 }}>
        <SnackBar
          open={open}
          alert={variant}
          handleClose={handleSnackBarClose}
          errorMsg={errMsg}
          successMsg={succMsg}
          mainMsg={mainMsg}
        />

        {showEditPage && editPromoCode && (
          <PagePromoCode
            promoCode={editPromoCode}
            setOpen={setOpen}
            setSuccMsg={setSuccMsg}
            setErrMsg={setErrMsg}
            setVariant={setVariant}
            setMainMsg={setMainMsg}
            setModalTriggerAPICalled={setModalTriggerAPICalled}
            handleCloseMorePromoCodes={handleCloseEditPage}
          />
        )}
        {!showEditPage && showDetails && selectedPromoCode && (
          <MorePromoCodes
            deleteModal={deleteModal}
            handleCloseMorePromoCodes={handleCloseDetails}
            promoCode={selectedPromoCode}
            handleDelete={handleDelete}
            handleOpenDeleteModal={handleOpenDeleteModal}
            handleCloseDeleteModal={handleCloseDeleteModal}
            handleOpenPagePromoCodes={handleOpenEditPage}
            role={role}
            operatorsMap={operatorsMap}
          />
        )}
        {!showEditPage && !showDetails && (
          <>
            <Box sx={{
              pb: 5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            }}
            >
              <Typography variant="h3">Promo Codes</Typography>
              <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>
                Manage Trek Rides Promo Codes
              </Typography>
            </Box>

            {loading && <CircularProgress color="success" sx={loadingStyle} />}

            <Box sx={tableStyle}>
              <Box sx={{
                mr: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
              >
                <UserListToolbar
                  numSelected={0}
                  filterName={filterName}
                  onFilterName={handleFilterByName}
                  labelName="Search"
                />
                {canManagePromoCodes && (
                  <CreateButton type="Promo Code" handleOpen={handleOpenCreateModal} />
                )}
              </Box>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 960 }}>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                      {filteredPromoCodes.map((row) => {
                        const {
                          _id,
                          code,
                          type,
                          status,
                          validityPeriodDays,
                          discountPercentage,
                        } = row;

                        return (
                          <TableRow key={_id || code} hover onClick={() => handleRowClick(row)}>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ color: theme.palette.grey[900] }}>
                                {code}
                              </Typography>
                            </TableCell>
                            <TableCell>{type || '—'}</TableCell>
                            <TableCell>
                              <Chip
                                label={status || 'Unknown'}
                                color={status === 'ACTIVE' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                {Array.isArray(row.assignedOperators)
                                  && row.assignedOperators.length > 0 ? (
                                    row.assignedOperators.map((opId) => (
                                      <Label
                                        key={opId}
                                        variant="soft"
                                        color="primary"
                                      >
                                        {operatorsMap[opId] || 'Unknown'}
                                      </Label>
                                    ))
                                  ) : (
                                    <Typography variant="body2" sx={{ color: theme.palette.grey[500] }}>
                                      No Operators
                                    </Typography>
                                  )}
                              </Stack>
                            </TableCell>
                            <TableCell>{validityPeriodDays ? `${validityPeriodDays} days` : '—'}</TableCell>
                            <TableCell>{discountPercentage !== undefined ? `${discountPercentage}%` : '—'}</TableCell>
                            <TableCell align="right">
                              <AdminMoreMenu
                                handleSeeMore={handleRowClick}
                                handleEditOpen={handleOpenEditPage}
                                data={row}
                                handleDeleteOpen={handleOpenDeleteModal}
                                handleClose={handleCloseDeleteModal}
                                deleteModal={deleteModal}
                                handleDelete={handleDelete}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {filteredPromoCodes.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                            <Typography variant="body1" sx={{ color: theme.palette.grey[600] }}>
                              No promo codes found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
              <CustomPagination
                page={page}
                totalNum={total}
                rowsPerPage={limit}
                handleChangePage={handleChangePage}
              />
            </Box>
          </>
        )}
      </Container>

      <CreatePromoCodeModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onCreate={handleCreatePromoCode}
        isCreating={creatingPromoCode}
      />
    </Page>
  );
}
